import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import { ListSubheader, MenuItem, MenuItemProps, Paper, Popper, styled } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { WithStyles } from "@mui/styles";
import createStyles from "@mui/styles/createStyles";
import withStyles from "@mui/styles/withStyles";
import { GetInputPropsOptions, GetItemPropsOptions, GetMenuPropsOptions, GetPropsCommonOptions } from "downshift";
import isEqual from "lodash/isEqual";
import uniqBy from "lodash/uniqBy";
import React from "react";
import { List, ListRowProps } from "react-virtualized";
import Checkbox from "shared/components/Checkbox";
import { ISelectOption, optionKey } from "shared/components/ISelectOption";
import TextField from "shared/components/TextField";
import { black, lightGray, lightGrayBorder } from "../../designSystem/theme";
import { selectAllOption } from "./MaterialAutocompleteMultiple";

export function equalValue(a: ISelectOption, b: ISelectOption): boolean {
  return isEqual(a.value, b.value);
}

export const PROGRESS_SIZE = 16;

interface IRenderOptionProps {
  option: ISelectOption;
  itemProps: Omit<MenuItemProps, "button">;
  isHighlighted: boolean;
  selectedItem: ISelectOption | ISelectOption[] | null | undefined;
  style: React.CSSProperties;
  withCheckbox?: boolean;
  allSelected?: boolean;
}

export function removeDuplicateValues(options: ISelectOption[]) {
  const uniqueOptions = uniqBy(options, option => optionKey(option.value));
  if (uniqueOptions.length !== options.length) {
    console.error(new Error("Autocomplete given options with duplicates in their values"));
  }
  return uniqueOptions;
}

export const itemToString = (option: ISelectOption | undefined) => (option ? option.label : "");

const StyledAddCircleIcon = styled(AddCircleIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const OptionLabel = styled("span")({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

function renderOptionContent(
  option: ISelectOption,
  isSelected: boolean,
  withCheckbox?: boolean,
  allSelected?: boolean,
) {
  if (withCheckbox) {
    return (
      <Checkbox
        preventDefaultClick
        label={option.label}
        value={isSelected || (option.value === selectAllOption.value && allSelected)}
      />
    );
  }

  if (option.create) {
    return (
      <>
        <StyledAddCircleIcon />
        <OptionLabel title={option.label}>{option.label}</OptionLabel>
      </>
    );
  }

  return <OptionLabel title={option.label}>{option.label}</OptionLabel>;
}

export const renderOption = ({
  option,
  itemProps,
  isHighlighted,
  selectedItem,
  style,
  withCheckbox = false,
  allSelected = false,
}: IRenderOptionProps) => {
  const isSelected = Array.isArray(selectedItem)
    ? selectedItem.some(item => item.value === option.value)
    : (selectedItem || "").toString().indexOf(option.label) > -1;

  return (
    <MenuItem
      {...itemProps}
      dense
      key={optionKey(option.value)}
      selected={isHighlighted}
      style={{
        fontWeight: isSelected ? 500 : 400,
        overflowY: "hidden",
        ...style,
      }}
      disabled={option.disabled}
      onClick={itemProps.onClick}
    >
      {renderOptionContent(option, isSelected, withCheckbox, allSelected)}
    </MenuItem>
  );
};

interface IGetRowRendererProps extends Pick<IRenderOptionProps, "selectedItem"> {
  options: ISelectOption[];
  highlightedIndex: number | null;
  itemClass: string;
  withCheckbox?: boolean;
  allSelected?: boolean;

  getItemProps(options: GetItemPropsOptions<ISelectOption>): any;
}

export const getRowRenderer = ({
  options,
  highlightedIndex,
  selectedItem,
  getItemProps,
  itemClass,
  withCheckbox = false,
  allSelected = false,
}: IGetRowRendererProps) => {
  return (rowProps: ListRowProps) => {
    const option = options[rowProps.index];
    const isHighlighted = rowProps.index === highlightedIndex;
    return renderOption({
      isHighlighted,
      option,
      selectedItem,
      itemProps: getItemProps({ index: rowProps.index, item: option, className: itemClass }),
      style: rowProps.style as React.CSSProperties,
      withCheckbox,
      allSelected,
    });
  };
};

export const getOptions = ({
  query,
  options: allOptions,
  selectedValues = [],
  removeSelected = false,
  includeSelectAll = false,
}: {
  query: string;
  options: ISelectOption[];
  selectedValues: ISelectOption["value"][];
  removeSelected?: boolean;
  includeSelectAll?: boolean;
}) => {
  const options = removeDuplicateValues(allOptions);

  const selectedKeys = new Set(selectedValues.map(optionKey));
  const notSelectedOptions = options.filter(option => !selectedKeys.has(optionKey(option.value)));

  const suggestedOptions = removeSelected
    ? notSelectedOptions.filter(option => filterOptionByQuery(option, query))
    : options.filter(option => filterOptionByQuery(option, query));

  if (!query && includeSelectAll && suggestedOptions.length > 0) return [selectAllOption, ...suggestedOptions];

  return suggestedOptions;
};

export const styles = (theme: Theme) =>
  createStyles({
    container: {
      display: "inline-block",
      position: "relative",
    },
    fullWidthContainer: {
      position: "relative",
      width: "100%",
    },

    selectedImgUrl: {
      width: 50,
      height: 50,
      marginRight: theme.spacing(1),
    },

    selectedImg: {
      objectFit: "contain",
    },

    progressContainer: {
      paddingTop: (30 - PROGRESS_SIZE) / 2,
      paddingRight: theme.spacing(1 / 2),
    },
  });

const optionMenuStyles = (theme: Theme) =>
  createStyles({
    popper: {
      zIndex: theme.zIndex.tooltip,
    },
    menuItem: {
      boxSizing: "border-box",
      transition: theme.transitions.create("background-color"),
      // Downshift highlights the hovered row (sets Mui-selected), so hover and
      // keyboard highlight share one "active row" colour.
      "&:hover, &.Mui-selected": {
        backgroundColor: lightGray,
      },
    },
  });

// Rounded, hairline-bordered dropdown panel with a soft shadow and a slim
// scrollbar on the virtualized list, replacing MUI's square default Paper.
const MenuPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 8,
  border: `1px solid ${lightGrayBorder}`,
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
  paddingBlock: theme.spacing(0.5),
  "& .ReactVirtualized__Grid": {
    "&::-webkit-scrollbar": {
      width: theme.spacing(1),
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: lightGrayBorder,
      borderRadius: theme.spacing(1),
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    borderRadius: "4px",
    paddingLeft: 0,

    "&.Mui-focused fieldset": {
      border: `1px solid black`,
    },
  },

  "& .MuiInputBase-input": {
    padding: theme.spacing(1),
    paddingLeft: 0,
  },

  "& .MuiSvgIcon-root": {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    fontSize: 22,
    color: black,
    transition: "color 0.2s ease-in-out",
  },

  "& .Mui-focused .MuiSvgIcon-root": {
    color: lightGrayBorder,
  },
}));

type WithCheckboxProps = {
  withCheckbox: true;
  getInputProps: (options: GetInputPropsOptions | undefined) => any;
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchQuery: string;
  allSelected: boolean;
  searchInputRef: React.RefObject<HTMLInputElement>;
};
type CheckboxProps = WithCheckboxProps | { withCheckbox: false };
type MenuProps = {
  options: ISelectOption[];
  allOptions: ISelectOption[];
  isOpen: boolean;
  anchorNode: HTMLDivElement | null | undefined;
  rowCount: number;
  rowHeight: number;
  highlightedIndex: number | null;
  selectedItemKey: ISelectOption | ISelectOption[] | null | undefined;
  getMenuProps: (options?: GetMenuPropsOptions, otherOptions?: GetPropsCommonOptions) => any;
  getItemProps: (options: GetItemPropsOptions<any>) => any;
  checkBoxProps?: CheckboxProps;
  withSearchBar?: boolean;
};

// The dropdown grows to fit the widest option so long values (e.g. campaign
// names) stay readable, but never narrower than the field and never past the
// cap. Width is estimated from label length to avoid measuring the DOM; labels
// that still exceed the cap fall back to the ellipsis in renderOption.
const APPROX_CHAR_WIDTH = 7.2;
const MENU_HORIZONTAL_PADDING = 48;
const MAX_MENU_WIDTH = 700;

function estimateMenuWidth(options: ISelectOption[], anchorWidth: number): number {
  const longestLabelLength = options.reduce((max, option) => Math.max(max, option.label.length), 0);
  const contentWidth = longestLabelLength * APPROX_CHAR_WIDTH + MENU_HORIZONTAL_PADDING;
  // Never narrower than the field; grow with content up to the cap (the cap is
  // applied before the floor so a field wider than the cap still wins).
  return Math.max(anchorWidth, Math.min(MAX_MENU_WIDTH, contentWidth));
}

function StyledOptionMenu({
  classes,
  options,
  allOptions,
  isOpen,
  anchorNode,
  rowCount,
  rowHeight,
  highlightedIndex,
  selectedItemKey,
  getMenuProps,
  getItemProps,
  withSearchBar = false,
  checkBoxProps = { withCheckbox: false },
}: MenuProps & WithStyles<typeof optionMenuStyles>) {
  const { withCheckbox } = checkBoxProps;

  const totalHeight = rowCount * rowHeight;
  const menuHeight = totalHeight > 300 ? 300 : totalHeight;
  const anchorWidth = anchorNode?.parentElement?.clientWidth ?? 0;
  const menuWidth = React.useMemo(() => estimateMenuWidth(allOptions, anchorWidth), [allOptions, anchorWidth]);

  return (
    <Popper
      className={classes.popper}
      open={isOpen}
      anchorEl={anchorNode}
      placement="bottom-start"
      modifiers={[
        {
          name: "flip",
          enabled: true,
        },
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            boundariesElement: "window",
          },
        },
      ]}
    >
      <MenuPaper
        {...(isOpen ? getMenuProps({ height: menuHeight }, { suppressRefError: true }) : {})}
        style={{ width: menuWidth }}
      >
        {withSearchBar && renderSearchBar(checkBoxProps as WithCheckboxProps)}

        <List
          tabIndex={-1}
          autoWidth
          autoContainerWidth
          width={menuWidth}
          height={menuHeight}
          rowHeight={rowHeight}
          rowCount={rowCount}
          scrollToIndex={highlightedIndex === null ? undefined : highlightedIndex}
          rowRenderer={getRowRenderer({
            options,
            getItemProps,
            highlightedIndex,
            itemClass: classes.menuItem,
            selectedItem: selectedItemKey,
            withCheckbox,
            allSelected: withCheckbox ? checkBoxProps.allSelected : false,
          })}
        />
      </MenuPaper>
    </Popper>
  );
}

function renderSearchBar({ onSearch, getInputProps, searchInputRef, searchQuery }: WithCheckboxProps) {
  return (
    <ListSubheader>
      <SearchField
        size="small"
        placeholder="Type to search..."
        fullWidth
        inputRef={searchInputRef}
        InputProps={{
          startAdornment: <SearchIcon />,
          ...getInputProps({
            onChange: onSearch,
            value: searchQuery,
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === "Tab") event.preventDefault();
            },
          }),
        }}
      />
    </ListSubheader>
  );
}

function filterOptionByQuery(option: ISelectOption, query: string): boolean {
  return !query || option.label.toLowerCase().indexOf(query.toLowerCase()) !== -1;
}

export const OptionMenu = withStyles(optionMenuStyles)(StyledOptionMenu);
